<template>
  <div class="modal-wrapper">
    <div class="modal-header">
      <div>Reorder Orders ({{ dateLabel }})</div>
      <i class="el-icon-close cursor-pointer" @click="$emit('close')" />
    </div>
    <div class="modal-content relative">
      <el-tabs v-model="activeTab" class="dense">
        <el-tab-pane v-for="group in groups" :key="group.key" :name="group.key">
          <span slot="label">
            {{ group.label }}
            <el-badge :type="group.badge" :value="lists[group.key].length" />
          </span>
          <div class="border border-gray-200 overflow-x-auto">
            <div
              class="reorder-row px-2 py-1 text-2xs font-semibold text-gray-500 bg-gray-50 border-b"
              :class="group.category"
            >
              <span class="text-center">
                <a
                  v-if="!isEmptyData(savedIds[group.key])"
                  class="no-underline font-bold red text-xs tracking-tighter"
                  @click="reset(group.key)"
                >
                  Reset
                </a>
              </span>
              <span class="text-center">#</span>
              <span>ID</span>
              <span class="text-center">Operations</span>
              <span class="text-center">Route EST</span>
              <span class="text-center">Route Status</span>
              <span v-if="group.category == 'drayage'">Street Turn</span>
              <span>CONT.#</span>
              <span>Warehouse</span>
              <span>City</span>
              <span>Ssline</span>
              <span>Size/Type</span>
            </div>
            <draggable
              v-model="lists[group.key]"
              class="stripe max-h-[60vh] overflow-auto"
              draggable=".stripe-item"
              ghost-class="ghost"
              animation="150"
              @change="save(group.key)"
            >
              <div
                v-for="(item, index) in lists[group.key]"
                :key="item.id"
                class="reorder-row stripe-item px-2 py-1.5 text-xs cursor-move select-none"
                :class="group.category"
              >
                <i class="el-icon-rank text-gray-500 text-center text-base" />
                <span class="text-center text-gray-400">{{ index + 1 }}</span>
                <a
                  @click.stop="openOrder(item.id)"
                  class="no-underline"
                  :class="{ red: item.hub_id != hubId }"
                >
                  {{ item.id }}
                </a>
                <div class="min-w-0" @click.stop>
                  <container-routes :item="item" :options="columnOptions" />
                </div>
                <center><route-est :item="item" /></center>
                <route-status :item="item" :options="columnOptions" />
                <drayage-street-turn
                  v-if="group.category == 'drayage'"
                  :item="item"
                  @click.native.stop="openStreetTurn"
                />
                <span class="truncate">{{ item.container_no }}</span>
                <span class="truncate text-gray-500 whitespace-pre-line">
                  {{ item.warehouse }}
                </span>
                <span class="truncate whitespace-pre-line">
                  {{ item.city }}
                </span>
                <span class="truncate">{{ item.ssline }}</span>
                <span class="truncate">{{ item.ctype }}</span>
              </div>
            </draggable>
            <div
              v-if="!lists[group.key].length"
              class="px-2 py-3 text-xs text-gray-400"
            >
              No orders
            </div>
          </div>
        </el-tab-pane>
      </el-tabs>
      <div class="absolute top-2 right-4">
        <el-dropdown size="small" @command="initSort">
          <el-button size="mini" type="primary" plain>
            Sort First
            <i class="el-icon-arrow-down el-icon--right"></i>
          </el-button>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="route_est"> Route EST </el-dropdown-item>
            <el-dropdown-item command="ssline"> SS Line </el-dropdown-item>
            <el-dropdown-item command="ctype"> Size/Type </el-dropdown-item>
            <el-dropdown-item command="warehouse">Whse. Name</el-dropdown-item>
            <el-dropdown-item command="city"> Whse. City </el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </div>
    </div>
  </div>
</template>
<script>
import Vue from "vue";
import { openDB } from "idb";
import { format, parse } from "date-fns";
import DrayageStreetTurn from "@/default/pages/containers/calendar/cells/drayage-street-turn.vue";
import ContainerRoutes from "@/default/pages/containers/routes.vue";
import RouteEst from "@/default/pages/containers/shared/route-est.vue";
import RouteStatus from "@/default/pages/containers/shared/route-status.vue";

const CALENDAR_ORDER_DB = "calendar-orders";
const CALENDAR_ORDER_STORE = "orders";
const GROUP_KEYS = ["IM", "EX", "TL", "XT"];
const GROUPS = [
  { key: "IM", label: "Import", badge: "primary", category: "drayage" },
  { key: "EX", label: "Export", badge: "warning", category: "drayage" },
  { key: "TL", label: "TL", badge: "info", category: "ltl" },
  { key: "XT", label: "XTown", badge: "primary", category: "drayage" },
];
let calendarOrderDb;

function openCalendarOrderDb() {
  if (!calendarOrderDb) {
    calendarOrderDb = openDB(CALENDAR_ORDER_DB, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(CALENDAR_ORDER_STORE)) {
          db.createObjectStore(CALENDAR_ORDER_STORE);
        }
      },
    }).catch((error) => {
      calendarOrderDb = null;
      throw error;
    });
  }
  return calendarOrderDb;
}

export function emptyOrderIds() {
  return { IM: [], EX: [], TL: [], XT: [] };
}

export function orderGroup(abbrev) {
  switch (abbrev) {
    case "IM":
    case "IBI":
      return "IM";
    case "EX":
    case "OBI":
      return "EX";
    case "TL":
    case "LTL":
      return "TL";
    case "XT":
      return "XT";
    default:
      return null;
  }
}

export function hasOrderIds(orderIds) {
  return GROUP_KEYS.some((key) => (orderIds?.[key] || []).length > 0);
}

export function groupOrderIds(stored, items = []) {
  if (Array.isArray(stored)) {
    const groups = emptyOrderIds();
    const byId = new Map(
      items.map((item) => [String(item.id), orderGroup(item.abbrev_type)]),
    );
    stored.forEach((id) => {
      const group = byId.get(String(id));
      if (group) groups[group].push(id);
    });
    return groups;
  }
  if (stored && typeof stored === "object") {
    const groups = emptyOrderIds();
    GROUP_KEYS.forEach((key) => {
      groups[key] = Array.isArray(stored[key]) ? stored[key].slice() : [];
    });
    return groups;
  }
  return emptyOrderIds();
}

export async function loadOrderIds(mark, items = []) {
  try {
    const db = await openCalendarOrderDb();
    const stored = await db.get(CALENDAR_ORDER_STORE, mark);
    const groups = groupOrderIds(stored, items);
    if (Array.isArray(stored) && items.length) {
      await db.put(CALENDAR_ORDER_STORE, groups, mark);
    }
    return groups;
  } catch (ex) {
    return emptyOrderIds();
  }
}

export async function saveOrderIds(mark, ids) {
  const db = await openCalendarOrderDb();
  await db.put(CALENDAR_ORDER_STORE, groupOrderIds(ids), mark);
}

export async function deleteOrderIds(mark) {
  const db = await openCalendarOrderDb();
  await db.delete(CALENDAR_ORDER_STORE, mark);
}

function setRank(item, rank) {
  if (item.rank !== rank) Vue.set(item, "rank", rank);
}

export function applyOrder(items, orderIds) {
  const groups = groupOrderIds(orderIds, items);
  if (!hasOrderIds(groups)) return;
  const positions = {};
  GROUP_KEYS.forEach((key) => {
    positions[key] = new Map(
      groups[key].map((id, index) => [String(id), index]),
    );
  });
  const buckets = emptyOrderIds();
  const rest = [];
  items.forEach((item, index) => {
    const group = orderGroup(item.abbrev_type);
    if (group) buckets[group].push({ item, index });
    else rest.push(item);
  });
  const ordered = [];
  GROUP_KEYS.forEach((key) => {
    const map = positions[key];
    const bucket = buckets[key];
    if (map.size > 0) {
      bucket.sort((a, b) => {
        const rank = (entry) =>
          map.has(String(entry.item.id))
            ? map.get(String(entry.item.id))
            : map.size + entry.index;
        return rank(a) - rank(b) || a.index - b.index;
      });
      bucket.forEach(({ item }, index) => setRank(item, index));
    }
    bucket.forEach(({ item }) => ordered.push(item));
  });
  items.splice(0, items.length, ...ordered, ...rest);
}

export function restoreGroupOrder(items, group, missions) {
  const buckets = emptyOrderIds();
  const rest = [];
  items.forEach((item) => {
    const key = orderGroup(item.abbrev_type);
    if (key) buckets[key].push(item);
    else rest.push(item);
  });
  buckets[group].forEach((item) => {
    const missionRank = missions.indexOf(item.mission);
    setRank(item, missionRank < 0 ? missions.length : missionRank);
  });
  buckets[group].sort((a, b) => a.rank - b.rank || Number(a.id) - Number(b.id));
  const ordered = [];
  GROUP_KEYS.forEach((key) => {
    buckets[key].forEach((item) => ordered.push(item));
  });
  items.splice(0, items.length, ...ordered, ...rest);
}

export function restoreMissionOrder(items, missions) {
  items.forEach((item) => {
    const missionRank = missions.indexOf(item.mission);
    setRank(item, missionRank < 0 ? missions.length : missionRank);
  });
  items.sort((a, b) => a.rank - b.rank || Number(a.id) - Number(b.id));
}

function reorderRow(item) {
  const warehouses = item.warehouses || [];
  const names = (rows, key) =>
    rows
      .map((row) => row && row[key])
      .filter(Boolean)
      .join("\n");
  return {
    id: item.id,
    hub_id: item.hub_id,
    abbrev_type: item.abbrev_type,
    transports: item.transports || [],
    warehouse: names(warehouses, "name"),
    city: names(warehouses, "city"),
    ssline: item.ssline && item.ssline.name,
    ctype: item.ctype,
    container_no: item.container_no,
    route_est: item.route_est,
    street_turn_from: item.street_turn_from || {},
    street_turn_to: item.street_turn_to || {},
  };
}

function sortGroup(list, orderIds, colorStyles) {
  const positions = new Map(
    (orderIds || []).map((id, index) => [String(id), index]),
  );
  if (positions.size === 0 && !colorStyles) return list;
  const sorted = list.slice();
  sorted.sort((a, b) => {
    const rank = (entry) => {
      if (positions.size === 0) return entry.item.rank ?? 0;
      return positions.has(String(entry.item.id))
        ? positions.get(String(entry.item.id))
        : positions.size + entry.index;
    };
    return rank(a) - rank(b) || a.index - b.index;
  });
  return sorted;
}

export function displayOrders(
  items,
  { orderIds = emptyOrderIds(), colorStyles = false } = {},
) {
  const groups = groupOrderIds(orderIds, items);
  const buckets = emptyOrderIds();
  items.forEach((item, index) => {
    const group = orderGroup(item.abbrev_type);
    if (group) buckets[group].push({ item, index });
  });
  const lists = emptyOrderIds();
  GROUP_KEYS.forEach((key) => {
    lists[key] = sortGroup(buckets[key], groups[key], colorStyles).map(
      ({ item }) => reorderRow(item),
    );
  });
  return lists;
}

export default {
  name: "ContainerCalendarReorder",
  components: { DrayageStreetTurn, ContainerRoutes, RouteEst, RouteStatus },
  props: {
    mark: {
      type: String,
      default: "",
    },
    hubId: {
      type: [Number, String],
      default: null,
    },
    orders: {
      type: Array,
      default: () => [],
    },
    colorStyles: {
      type: Boolean,
      default: false,
    },
    onOrdered: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      groups: GROUPS,
      activeTab: "IM",
      lists: emptyOrderIds(),
      savedIds: emptyOrderIds(),
    };
  },
  computed: {
    columnOptions() {
      return {
        curApptDate: /^\d{4}-\d{2}-\d{2}$/.test(this.mark) ? this.mark : null,
      };
    },
    dateLabel() {
      switch (this.mark) {
        case "not_sched":
          return "Not Sched.";
        case "upcoming_loads":
          return "Upcoming";
        default:
          return format(
            parse(this.mark, "yyyy-MM-dd", new Date()),
            "EEE MM/dd",
          );
      }
    },
  },
  async created() {
    this.savedIds = await loadOrderIds(this.mark, this.orders);
    this.lists = displayOrders(this.orders, {
      orderIds: this.savedIds,
      colorStyles: this.colorStyles,
    });
    this.activeTab =
      GROUPS.find((group) => this.lists[group.key].length)?.key || "IM";
  },
  methods: {
    openOrder(id) {
      const options = this.viewOrderOptions(id);
      Dialog.open({ width: "900px", clickToClose: true, ...options });
    },
    openStreetTurn(event) {
      const link = event.target.closest("[data-order-id]");
      const id = link && link.getAttribute("data-order-id");
      if (id) this.openOrder(id);
    },
    async initSort(command) {
      const group = this.activeTab;
      const sortLabel =
        {
          route_est: "Route EST",
          ssline: "SS Line",
          ctype: "Size/Type",
          warehouse: "Warehouse Name",
          city: "Warehouse City",
        }[command] || command;
      try {
        await this.$confirm(
          `Do you want to sort orders first then drag rows afterward to adjust?`,
          `Sort by ${sortLabel}`,
        );
      } catch (ex) {
        return;
      }
      const sorted = this._.orderBy(this.lists[group], command);
      this.lists[group].splice(0, this.lists[group].length, ...sorted);
      this.save(group);
    },
    async save(group) {
      const visibleIds = this.lists[group].map((item) => item.id);
      const visible = new Set(visibleIds.map((id) => String(id)));
      const hiddenIds = this.savedIds[group].filter(
        (id) => !visible.has(String(id)),
      );
      const next = {
        ...this.savedIds,
        [group]: visibleIds.concat(hiddenIds),
      };
      try {
        await saveOrderIds(this.mark, next);
        this.savedIds = next;
        if (this.onOrdered) this.onOrdered(next);
      } catch (ex) {
        this.$message({ message: "Could not save the order", type: "error" });
      }
    },
    async reset(group) {
      const label = GROUPS.find((item) => item.key === group).label;
      try {
        await this.$confirm(
          `Put these orders back in the default order for ${this.dateLabel}?`,
          `Reset ${label}`,
        );
      } catch (ex) {
        return;
      }
      const next = { ...this.savedIds, [group]: [] };
      try {
        if (hasOrderIds(next)) await saveOrderIds(this.mark, next);
        else await deleteOrderIds(this.mark);
        this.savedIds = next;
        const orders = this.onOrdered
          ? this.onOrdered(next, group)
          : this.orders;
        this.lists = displayOrders(orders || this.orders, {
          orderIds: next,
          colorStyles: true,
        });
      } catch (ex) {
        this.$message({ message: "Could not reset the order", type: "error" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.reorder-row {
  @apply grid gap-x-2 items-center;
  &.drayage {
    grid-template-columns:
      16px 28px 48px minmax(220px, 1.6fr) 48px 150px 96px 90px
      minmax(110px, 1fr) 88px 100px 52px;
  }
  &.ltl {
    grid-template-columns:
      16px 28px 48px minmax(220px, 1.6fr) 48px 150px 90px
      minmax(110px, 1fr) 88px 100px 52px;
  }
}
</style>
